import { Modal } from "@appkit4/react-components";
import { useEffect, useState } from "react";
import { Button, CalendarPicker } from "../../components/ReadonlyAwareInputs";
import { getSelectedPeriod } from "../../services/GroupContext";
import { processDateJS } from "../../utils/dateProcessor";
import "./entityInput.scss";
import { isPeriodMoreThan12Months } from "./util/entityInputUtils";

const MADElectionModal = ({ isVisible, setIsVisible, onClick }) => {
  const selectedPoA = getSelectedPeriod();
  const { period_start, period_end } = selectedPoA;

  const [actualStartDate, setActualStartDate] = useState(undefined);
  const [actualEndDate, setActualEndDate] = useState(undefined);

  const [actualStartError, setActualStartError] = useState("");
  const [actualEndError, setActualEndError] = useState("");

  const getPopulatedDate = (date) => {
    return isPeriodMoreThan12Months(selectedPoA)
      ? undefined
      : processDateJS(date);
  };

  useEffect(() => {
    const populatedStartDate = getPopulatedDate(period_start);
    const populatedEndDate = getPopulatedDate(period_end);

    setActualStartDate((prev) => prev || populatedStartDate);
    setActualEndDate((prev) => prev || populatedEndDate);
  }, [isVisible]);

  const lastValidEndDate = processDateJS(actualStartDate)
    ?.add(12, "month")
    ?.subtract(1, "day");

  const isBeforePoAStart = (date) => {
    return date?.isBefore(processDateJS(period_start));
  };

  const checkActualStartError = (actualStartDate) => {
    const isActualBeforePoAStart = isBeforePoAStart(actualStartDate);
    const isAfterPoAEnd = actualStartDate?.isAfter(processDateJS(period_end));
    const isAfterActualEnd =
      actualStartDate &&
      actualEndDate &&
      actualStartDate?.isAfter(actualEndDate);

    let errorMessage = [];
    if (isActualBeforePoAStart) errorMessage.push("before PoA start");
    if (isAfterPoAEnd) errorMessage.push("after PoA end");
    if (isAfterActualEnd) errorMessage.push("after actual end date");
    errorMessage =
      errorMessage.length > 0
        ? "Actual start date must not be " + errorMessage.join(" or ") + "."
        : "";

    setActualStartError(errorMessage);
  };

  const checkActualEndError = (actualEndDate) => {
    const isActualBeforePoAStart = isBeforePoAStart(actualEndDate);
    const isAfterLastValidEndDate = actualEndDate?.isAfter(lastValidEndDate);
    const isBeforeActualStart =
      actualStartDate &&
      actualEndDate &&
      actualEndDate?.isBefore(actualStartDate);

    let errorMessage = [];
    if (isActualBeforePoAStart) errorMessage.push("before PoA start");
    if (isAfterLastValidEndDate)
      errorMessage.push("more than 12 months after the actual start date");
    if (isBeforeActualStart) errorMessage.push("before actual start date");
    errorMessage =
      errorMessage.length > 0
        ? "Actual end date must not be " + errorMessage.join(" or ") + "."
        : "";

    setActualEndError(errorMessage);
  };

  const confirmButtonDisabled =
    actualStartError || actualEndError || !actualStartDate || !actualEndDate;

  const resetDates = () => {
    setActualStartDate(undefined);
    setActualEndDate(undefined);
  };

  const handleCancel = () => {
    resetDates();
    setIsVisible(false);
  };

  const handleConfirm = () => {
    setIsVisible(false);
    onClick(actualStartDate, actualEndDate);
  };

  const handleActualStartChange = (value) => {
    const newActualStartDate = processDateJS(value);
    checkActualStartError(newActualStartDate);
    setActualStartDate(newActualStartDate);
  };

  const handleActualEndChange = (value) => {
    const newActualEndDate = processDateJS(value);
    checkActualEndError(newActualEndDate);
    setActualEndDate(newActualEndDate);
  };

  return (
    <Modal
      wrapperStyle={{ zIndex: "5000" }}
      header={
        <p className="large no-margin">
          Enter Actual Accounting Period Start and End Dates
        </p>
      }
      ariaLabel={"AP Actual Dates"}
      onCancel={handleCancel}
      visible={isVisible}
      modalStyle={{ width: "40rem" }}
      footer={
        <>
          <Button kind="secondary" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            kind="primary"
            onClick={handleConfirm}
            disabled={confirmButtonDisabled}
          >
            Confirm
          </Button>
        </>
      }
    >
      <div className="dates-container">
        <CalendarPicker
          className="col"
          fieldTitle="Actual AP Start Date"
          minDate={processDateJS(period_start)}
          maxDate={processDateJS(period_end)}
          fieldWidth="100%"
          value={actualStartDate}
          placeholder="dd/mm/yyyy"
          format="DD/MM/YYYY"
          error={actualStartError}
          useCustomValidation
          customErrorNode={actualStartError}
          onChange={handleActualStartChange}
        />

        <CalendarPicker
          className="col"
          key={actualStartDate}
          fieldTitle="Actual AP End Date"
          minDate={actualStartDate}
          maxDate={lastValidEndDate}
          fieldWidth="100%"
          value={actualEndDate}
          placeholder="dd/mm/yyyy"
          format="DD/MM/YYYY"
          error={actualEndError}
          useCustomValidation
          customErrorNode={actualEndError}
          onChange={handleActualEndChange}
        />
      </div>

      <p className="note">
        Note: Any manually edited Accounting Periods with matching dates will be
        overwritten.
      </p>
    </Modal>
  );
};

export default MADElectionModal;
